'use client'

import { mapCreatorCampaignToCampaign } from '@/app/[locale]/(landing-page)/community-threads/_data/map-creator-campaign'
import { Button } from '@/components/ui/button'
import { useCreatorCampaigns } from '@/hooks/react-query/community-v2/use-creator-campaigns.query'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { cn } from '@/lib/utils'
import type { Campaign } from '@/types/campaign'
import { ArrowRight } from 'lucide-react'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import Link from 'next/link'
import { useMemo } from 'react'

type Props = {
  gaTrackingPrefix: string
}

// Ưu tiên campaign đang mở đăng ký, không có thì lấy campaign đầu danh sách
const pickCampaign = (campaigns: readonly Campaign[]) =>
  campaigns.find((campaign) => campaign.status === 'registration_open') ??
  campaigns[0]

export const CommunityCampaignCard = ({ gaTrackingPrefix }: Props) => {
  const t = useTranslations()
  const { data, isLoading } = useCreatorCampaigns()

  const campaign = useMemo(
    () => pickCampaign((data ?? []).map(mapCreatorCampaignToCampaign)),
    [data]
  )

  if (isLoading) {
    return (
      <div className='h-[540px] animate-pulse rounded-lg bg-neutral-100 lg:h-auto lg:flex-1' />
    )
  }

  if (!campaign) return null

  // Mở thẳng popup đăng ký campaign ở trang community-threads
  const registerHref = `/community-threads?register_campaign=${campaign.slug}`
  const isOpen = campaign.status === 'registration_open'

  return (
    <div className='flex flex-col gap-3 rounded-lg border border-neutral-200 p-4 lg:flex-1 lg:flex-row lg:gap-6 lg:p-6'>
      <Link
        href={registerHref}
        className='relative block h-[232px] shrink-0 overflow-hidden rounded lg:h-auto lg:w-[260px]'
        ga-tracking-value={`${gaTrackingPrefix}__campaign__image`}
        ga-tracking-label={campaign.title}
      >
        {campaign.banner && (
          <Image
            src={ImageHelper.getN7ImageUrl(campaign.banner)}
            alt={campaign.title}
            fill
            sizes='(min-width: 1024px) 260px, 100vw'
            className='object-cover'
          />
        )}
      </Link>
      <div className='flex flex-1 flex-col justify-between gap-4'>
        <div className='flex flex-col items-start gap-3'>
          <span
            className={cn(
              'rounded-full px-2.5 py-1 text-xs font-medium leading-4',
              isOpen
                ? 'bg-success-50 text-success'
                : 'bg-neutral-100 text-neutral-700'
            )}
          >
            {campaign.statusLabel}
          </span>
          <h3 className='font-criteria text-2xl font-semibold leading-[35px] tracking--2 text-neutral-900 lg:text-[28px] lg:font-normal lg:leading-[41px]'>
            {campaign.title}
          </h3>
          <p className='text-sm leading-5 text-neutral-600'>
            {campaign.subtitle ||
              t('home_page.community.campaign_description')}
          </p>
          <p className='text-sm font-medium leading-5 text-neutral-900'>
            {t('home_page.community.register_time', {
              start: campaign.registerStart,
              end: campaign.registerEnd,
            })}
          </p>
        </div>
        <Button asChild variant='black' className='w-fit'>
          <Link
            href={registerHref}
            ga-tracking-value={`${gaTrackingPrefix}__campaign__register`}
            ga-tracking-label={t('button.register_now')}
          >
            {t('button.register_now')}
            <ArrowRight strokeWidth={1.5} />
          </Link>
        </Button>
      </div>
    </div>
  )
}
